import { useEffect, useRef, useState } from "react";

import useService from "../../../../hooks/useService";
import { useNotesModalStore } from "../../Common/ClientNoteTrigger/store/useNotesModalStore";
import convertKeysToCamelCase from "../helper/helperFunctions";
import { SORT_OPTS, STRINGS } from "../utils/strings";

export default function useClientNotes(caseId, handlers, customURLPath, options = {}) {
  const {
    notesRepositoryKey = "repositories/case-notes-repository",
    ownerQueryKey = "case",
    includeOwnerInCreatePayload = true,
  } = options;
  const notesRepository = useService(notesRepositoryKey);
  const { open } = useNotesModalStore();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(STRINGS.DEFAULT_PAGINATION_LIMIT);
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortValue, setSortValue] = useState(SORT_OPTS.find((option) => option.default)?.value);
  const prevSearchRef = useRef(searchText);
  const prevSortRef = useRef(sortValue);
  const prevCaseIdRef = useRef(caseId);
  const ownerId = caseId;

  useEffect(() => {
    if (ownerId) {
      const searchChanged = prevSearchRef.current !== searchText;
      const sortChanged = prevSortRef.current !== sortValue;
      const caseChanged = prevCaseIdRef.current !== ownerId;

      if (!customURLPath && offset !== 0 && (searchChanged || sortChanged || caseChanged)) {
        setOffset(0);
      } else {
        fetchClientNotesData();
      }

      prevSearchRef.current = searchText;
      prevSortRef.current = sortValue;
      prevCaseIdRef.current = ownerId;
    }
  }, [ownerId, searchText, sortValue, limit, offset, customURLPath]);

  useEffect(() => {
    if (open) {
      handleOnClickAddBtn();
    }
  }, [open]);

  const removeTags = (str) => str?.toString().replace(/(<([^>]+)>)/gi, "") || "";

  const fetchClientNotesData = async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        [ownerQueryKey]: caseId,
        ordering: sortValue,
      };
      if (searchText.trim()) {
        queryParams.search = searchText.trim();
      }
      if (!customURLPath) {
        queryParams.limit = limit;
        queryParams.offset = offset;
      }
      if (customURLPath) {
        delete queryParams[ownerQueryKey];
      }

      let responseData = convertKeysToCamelCase(
        await notesRepository.query(queryParams, customURLPath)
      );

      if (customURLPath) {
        responseData = responseData?.readOnly?.records;
        setData(responseData || []);
        setTotalCount(responseData?.length || 0);
        return;
      }

      const count = await notesRepository.getTotalCount();
      setTotalCount(count || 0);
      setData(responseData || []);
    } catch (error) {
      console.error(error);
      handlers.openFailureModal(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnClickEditBtn = (editObj) => {
    setMode(STRINGS.EDIT_MODE);
    setSelectedNote(editObj);
    setOpenModal(true);
  };

  const handleOnClickAddBtn = () => {
    setMode(STRINGS.ADD_MODE);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setMode(null);
    setSelectedNote(null);
    setOpenModal(false);
  };

  const handlePinNote = async (id, pinnedVal) => {
    try {
      await notesRepository.patch(id, { pinned: pinnedVal }, caseId);
      handlers.openSuccessModal();
      fetchClientNotesData();
    } catch (error) {
      console.error(error);
      handlers.openFailureModal(error);
    }
  };

  const handleSubmit = async (noteId, modeVal, data) => {
    try {
      let responseObj;

      if (modeVal === STRINGS.ADD_MODE) {
        const createPayload = includeOwnerInCreatePayload
          ? {
              ...data,
              [ownerQueryKey]: caseId,
            }
          : data;

        responseObj = await notesRepository.createRecord(caseId, createPayload);
      } else if (modeVal === STRINGS.EDIT_MODE) {
        responseObj = await notesRepository.patch(noteId, data, caseId);
      }

      if (responseObj) {
        handleCloseModal();
        handlers.openSuccessModal();
        fetchClientNotesData();
      }
    } catch (error) {
      console.error(error);
      handlers.openFailureModal(error);
    }
  };

  return {
    data,
    isLoading,
    mode,
    openModal,
    searchText,
    selectedNote,
    currentPage: Math.floor(offset / limit) + 1,
    sortValue,
    resultLimit: limit,
    totalCount,
    fetchClientNotesData,
    handlePageChange: (_, value) => setOffset(value ? (value - 1) * limit : 0),
    handleCloseModal,
    handleOnClickAddBtn,
    handleOnClickEditBtn,
    handlePinNote,
    handleSubmit,
    removeTags,
    setSearchText,
    setSortValue,
  };
}
